import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getFileUrl } from '@weblite/file'
import { Timestamp } from '@weblite/ts-action/types'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { captureKeyboardEvents } from '@weblite/logic-toolkit'
import { EmbeddedLink, RawVideoMessage } from '@weblite/ts-action/message'
import { MediaCard } from '$common/media-card'
import { AppBarBackTitleActionMenu } from '$common/app-bar/app-bar-back-title-action-menu'
import { useContentFileStatus } from '$common/react-hooks/message'
import { formatDuration } from '$func/time'
import { nothing } from '$func/utils'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { AudioPlayerApi } from '$common/audio-player'
import { VideoPlayer } from './video-player'

const AUTO_HIDE_DURATION = 3000

export interface VideoDetails {
  src?: string
  description?: string
  owner?: {
    id: string
    type: 'USER' | 'WAPP' | 'CHANNEL'
  }
  date?: Timestamp
  functions?: {
    onDownload?: () => unknown
    onShare?: () => unknown
    onRemove?: () => unknown
    onForward?: () => unknown
    onShowSharedMedia?: () => unknown
    onEdit?: () => unknown
  }
  attachment?: RawVideoMessage['attachment']
  links?: EmbeddedLink[]
}

interface Props {
  media: VideoDetails
  total?: number
  current?: number
  onPrev?: () => void
  onNext?: () => void
  loading?: boolean
}

export function VideoPlayerModal({
  media,
  current,
  total,
  onPrev,
  onNext,
  loading = false,
}: Props) {
  const {
    src,
    description,
    owner,
    date,
    functions,
    attachment,
    links = [],
  } = media || {}

  const {
    onShare,
    onShowSharedMedia,
    onDownload,
    onForward,
    onRemove,
    onEdit,
  } = functions || {}

  const { base64: thumbnail = '', fileId } = attachment || {}
  const { blobURL, startDownload, state } = useContentFileStatus({
    fileId: fileId || '',
    uploadId: '',
    messageType: 'VIDEO',
  })

  const blobURLAvailableAtMountRef = useRef(!!blobURL)

  const source = useMemo(
    () =>
      blobURLAvailableAtMountRef.current
        ? blobURL
        : src || getFileUrl(fileId || ''),
    [fileId, src, blobURL],
  )

  const cardRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const playButtonRef = useRef<HTMLButtonElement>(null)
  const captionRef = useRef<HTMLDivElement>(null)
  const appbarRef = useRef<HTMLDivElement>(null)
  const timer = useRef<NodeJS.Timeout>()
  const isPlaying = useRef(true)

  const [sliderValue, setSliderValue] = useState(0)
  const [duration, setDuration] = useState((attachment?.duration || 0) * 1000)
  const [showSender, setShowSender] = useState(false)

  useEffect(() => {
    setShowSender(!isPlaying.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying.current])

  const videoDurationText = formatDuration(duration)
  const spentDurationText = formatDuration((sliderValue / 100) * duration || 0)

  useEffect(() => {
    if (src || blobURL || state === 'DOWNLOADING') return
    startDownload()
  }, [src, blobURL, state, startDownload])

  const handlePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    isPlaying.current = true
    video.play()?.catch(nothing)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      concealDetails()
    }, AUTO_HIDE_DURATION)
    setShowSender(false)
  }, [])

  const handlePause = () => {
    if (videoRef.current) {
      isPlaying.current = false
      videoRef.current.pause()
    }
  }

  const handleLoadedMetadata = useCallback(() => {
    if (!videoRef.current) return
    videoRef.current.click()

    setDuration(videoRef.current.duration * 1000)
  }, [])

  const handleSliderChange = (value: number) => {
    if (videoRef.current)
      videoRef.current.currentTime = (value / 100000) * duration
    setSliderValue(value)
  }

  const handleTimeUpdate = () => {
    if (videoRef.current)
      setSliderValue((videoRef.current.currentTime / (duration / 1000)) * 100)
  }

  const handleEnd = () => {
    isPlaying.current = false
    clearTimeout(timer.current)
    setSliderValue(0)
    revealDetails()
    setShowSender(true)
  }

  const revealDetails = (justPlayButton = false) => {
    const playButton = playButtonRef.current
    const caption = captionRef.current
    const appbar = appbarRef.current
    if (playButton && caption && appbar) {
      playButtonRef.current.style.visibility = 'visible'
      if (justPlayButton) return
      captionRef.current.style.visibility = 'visible'
      appbarRef.current.style.visibility = 'visible'
    }
  }

  const concealDetails = () => {
    if (playButtonRef.current) playButtonRef.current.style.visibility = 'hidden'
    if (captionRef.current) captionRef.current.style.visibility = 'hidden'
    if (appbarRef.current) appbarRef.current.style.visibility = 'hidden'
  }

  const handleShowControl = useCallback((justPlayButton = false) => {
    clearTimeout(timer.current)
    revealDetails(justPlayButton)
    timer.current = setTimeout(() => {
      if (isPlaying.current || justPlayButton) concealDetails()
    }, AUTO_HIDE_DURATION)
  }, [])

  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      handleShowControl(true)
      if (isPlaying.current) {
        handlePause()
      } else {
        handlePlay()
      }
    }
  }, [handleShowControl, handlePlay])

  useEffect(() => {
    return captureKeyboardEvents({
      onSpaceBar: togglePlay,
      onEsc: () => {
        closeCurrentModal()
      },
    })
  }, [togglePlay])

  useEffect(() => {
    handleShowControl()
    AudioPlayerApi.pause()
    // TODO: Implementing a retry method is necessary since playing the video will not currently function if a user is not online and it is not cached.
    handlePlay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MediaCard
      onClick={() => handleShowControl()}
      appbarRef={appbarRef}
      appbarComponent={
        <AppBarBackTitleActionMenu
          title={
            total && current && current > 0
              ? `${toLocale(current)} ${t('common.from')} ${toLocale(total)}`
              : t('message.VIDEO')
          }
          onBack={closeCurrentModal}
          functions={{
            onRemove,
            onDownload,
            onForward,
            onShowSharedMedia,
            onEdit,
          }}
          loading={loading}
        />
      }
      cardRef={cardRef}
    >
      <VideoPlayer
        src={source}
        poster={thumbnail}
        date={date}
        owner={owner}
        description={description}
        showCaption={showSender}
        sliderValue={sliderValue}
        videoDurationText={videoDurationText}
        spentDurationText={spentDurationText}
        isPlaying={isPlaying}
        playButtonRef={playButtonRef}
        videoRef={videoRef}
        videoContainerRef={videoContainerRef}
        captionRef={captionRef}
        onTimeUpdate={handleTimeUpdate}
        onPause={handlePause}
        onPlay={handlePlay}
        onSliderValueChange={handleSliderChange}
        onShare={onShare}
        onEnded={handleEnd}
        onPrev={onPrev}
        onNext={onNext}
        links={links}
        onLoadedMetadata={handleLoadedMetadata}
      />
    </MediaCard>
  )
}
